import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { WidgetDefinitionProps, WidgetKeys, WidgetWrapperProps } from 'jamespot-user-api';
import { Widget } from 'jamespot-front-business';
import { JRCModalLayoutProps } from 'jamespot-react-components';
import { getBuilderWidgets } from '../namespace';
import {
    CSSSquareWidget,
    CSSSquareWidgetWrapper,
    CSSWidgetButton,
    CSSWrapTitle,
    CSSWrapWidget,
    CSSWrapWidgetWrapper,
    Flex,
} from './WidgetList.style';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';

const JRCButton = commonComponentsRegistry.getLazyComponent('Button');
const JRCModalLayout = commonComponentsRegistry.getLazyComponent('ModalLayout');
const JRCModalContent = commonComponentsRegistry.getLazyComponent('ModalContent');
const JRCTooltip = commonComponentsRegistry.getLazyComponent('Tooltip');
const JRCIconButton = commonComponentsRegistry.getLazyComponent('IconButton');

type WidgetListMode = 'button' | 'list' | 'gallery' | 'custom' | 'article' | 'comment';

/*
    Widget Builder
*/
export const WidgetList = ({
    mode,
    view,
    children,
    namespace = 'default',
    onChange,
}: {
    mode?: WidgetListMode;
    view?: string;
    children?: any;
    namespace?: string;
    onChange: (widget: WidgetWrapperProps) => void;
}) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const widgets = getBuilderWidgets(namespace);
    const intl = useIntl();

    const args: JRCModalLayoutProps = {
        open: isOpen,
        portalId: 'react-modal',
        variant: 'full-height',
        closeHandler: () => setOpen(false),
    };

    const onClick = (name: WidgetKeys) => {
        onChange(Widget.factory(name));
        setOpen(false);
    };
    return (
        <>
            <JRCModalLayout {...args}>
                <JRCModalContent
                    onClickIconClose={args.closeHandler}
                    buttons={
                        mode === 'comment'
                            ? [
                                  {
                                      children: <FormattedMessage id="GLOBAL_Close" />,
                                      color: 'cancel',
                                      clickHandler: () => setOpen(false),
                                  },
                              ]
                            : undefined
                    }
                    title={intl.formatMessage({ id: 'WIDGET_Builder' })}>
                    {mode === 'comment' ? (
                        <CSSWrapWidgetWrapper column>
                            {widgets.map((widget, index) => (
                                <CSSWrapWidget
                                    key={widget.name}
                                    open={true}
                                    full
                                    parentIsColumn
                                    index={index}
                                    onClick={() => onClick(widget.name)}>
                                    <Flex direction={'row'} gap={12}>
                                        <img src={widget.img} />
                                        <Flex direction={'column'}>
                                            <h3>{intl.formatMessage({ id: widget.label })}</h3>
                                            <p>{intl.formatMessage({ id: widget.description })}</p>
                                        </Flex>
                                    </Flex>
                                </CSSWrapWidget>
                            ))}
                        </CSSWrapWidgetWrapper>
                    ) : (
                        <CSSSquareWidgetWrapper>
                            {widgets.map((widget, index: number) => {
                                return (
                                    <CSSSquareWidget
                                        key={index}
                                        onClick={() => onClick(widget.name)}
                                        available={widget.available}>
                                        <img src={widget.img} />
                                        <div className="show-on-hover">{intl.formatMessage({ id: widget.label })}</div>
                                    </CSSSquareWidget>
                                );
                            })}
                        </CSSSquareWidgetWrapper>
                    )}
                </JRCModalContent>
            </JRCModalLayout>
            {mode === 'custom' && children ? <div onClick={() => setOpen(true)}>{children}</div> : <></>}
            {mode === 'button' && view === 'edit' ? (
                <JRCButton onClick={() => setOpen(true)} variant={'outlined'} minWidth={'100%'}>
                    {intl.formatMessage({ id: 'WIDGET_Add_Widget' })}
                </JRCButton>
            ) : (
                <></>
            )}
            {mode === 'gallery' && view === 'edit' ? (
                <CSSSquareWidgetWrapper>
                    {widgets.map((widget, index: number) => {
                        return (
                            <CSSSquareWidget
                                key={index}
                                onClick={() => onClick(widget.name)}
                                available={widget.available}>
                                <img src={widget.img} />
                                <div className="show-on-hover">{intl.formatMessage({ id: widget.label })}</div>
                            </CSSSquareWidget>
                        );
                    })}
                </CSSSquareWidgetWrapper>
            ) : (
                <></>
            )}
            {mode === 'article' && view === 'edit' ? <WidgetModeArticle widgets={widgets} onClick={onClick} /> : <></>}
            {mode === 'comment' && view === 'edit' ? (
                <WidgetModeComment
                    onClick={() => {
                        setOpen(true);
                    }}
                />
            ) : (
                <></>
            )}
        </>
    );
};

const HEIGHT_CLOSED = 50;
const HEIGHT = 80;
const MARGIN_BOTTOM = 10;

const WidgetModeArticle = ({
    widgets,
    onClick,
}: {
    widgets: WidgetDefinitionProps[];
    onClick: (name: WidgetKeys) => void;
}) => {
    const [open, isOpen] = useState(false);
    const intl = useIntl();

    const calcHeight = useMemo(() => {
        return Math.ceil(widgets.length / 2) * HEIGHT + HEIGHT_CLOSED + MARGIN_BOTTOM;
    }, [widgets]);

    return (
        <>
            <CSSWidgetButton open={open} height={calcHeight}>
                <CSSWrapTitle onClick={() => isOpen(!open)}>
                    {intl.formatMessage({ id: 'WIDGET_Add_Widget' })}
                </CSSWrapTitle>
                <CSSWrapWidgetWrapper>
                    {widgets.map((widget, index) => {
                        return (
                            <CSSWrapWidget
                                key={widget.name}
                                open={open}
                                index={index}
                                onClick={() => onClick(widget.name)}>
                                <img src={widget.img} />
                                {intl.formatMessage({ id: widget.label })}
                            </CSSWrapWidget>
                        );
                    })}
                </CSSWrapWidgetWrapper>
            </CSSWidgetButton>
        </>
    );
};

const WidgetModeComment = ({ onClick }: { onClick: () => void }) => {
    const intl = useIntl();
    return (
        <>
            <JRCTooltip description={intl.formatMessage({ id: 'COMMENT_Add_Widget' })}>
                <JRCIconButton
                    bg={'grey1'}
                    bgHover={'grey2'}
                    color={'grey5'}
                    iconSize={22}
                    onClick={() => {
                        onClick();
                    }}
                    icon={'icon-fs-puzzle'}
                />
            </JRCTooltip>
        </>
    );
};
